import {
  countryToApiId,
  getCountryByApiId,
  getDefaultCountry,
  isCountryEnabled,
  joinPhone,
  resolveCountryName,
  resolveDialForCountryName,
  splitPhone,
} from "./countries";
import { USER_TYPE_CLIENT, USER_TYPE_DEALER } from "./userTypes";

export const BUSINESS_TYPE_OPTIONS = [
  { value: "1", label: "Franchise Dealer" },
  { value: "2", label: "Independent Dealer" },
  { value: "3", label: "Lease" },
  { value: "4", label: "Trade" },
  { value: "5", label: "Other" },
];

export const NEXT_GEAR_OPTIONS = [
  { value: "1", label: "Yes" },
  { value: "2", label: "No" },
];

export const EMPTY_CLIENT_PROFILE_FORM = {
  first_name: "",
  last_name: "",
  address: "",
  country: getDefaultCountry().name,
  postcode: "",
  phone_dial: getDefaultCountry().dial,
  phone_national: "",
};

/** Same phone/country shape as client, plus dealer-only fields. */
export const EMPTY_DEALER_PROFILE_FORM = {
  first_name: "",
  last_name: "",
  company_name: "",
  address: "",
  city: "",
  county: "",
  country: getDefaultCountry().name,
  postcode: "",
  phone_dial: getDefaultCountry().dial,
  phone_national: "",
  garagecode: "",
  /** Dealer business category (1–5), same as legacy dealers profile `type`. */
  business_type: "1",
  client_type: "2",
};

function pickCountryValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    if (value.name) return String(value.name);
    if (value.code) return String(value.code);
    if (value.id != null) return String(value.id);
    return "";
  }
  return String(value);
}

function pickIdValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object" && value.id != null) return String(value.id);
  return String(value);
}

function resolveProfileCountryName(rawCountry) {
  const picked = pickCountryValue(rawCountry);
  const fromApi = getCountryByApiId(picked);
  let countryName = fromApi?.name || resolveCountryName(picked);
  if (!isCountryEnabled(countryName)) {
    countryName = getDefaultCountry().name;
  }
  return countryName;
}

function mapPhoneFields(user = {}, countryName) {
  const rawMobile = String(user.mobile_number || user.phone || "").trim();
  const phone = splitPhone(rawMobile);
  const hasDialPrefix = rawMobile.startsWith("+") || rawMobile.startsWith("00");
  const dial = hasDialPrefix
    ? phone.dial
    : resolveDialForCountryName(countryName, phone.dial);
  return {
    phone_dial: dial,
    phone_national: phone.national,
  };
}

/** Extract county option list from getUpdateUser response. */
export function pickProfileLocationLists(payload = {}) {
  const root =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const county = root?.county || payload?.county || [];
  const country = root?.country || payload?.country || [];
  return {
    county: Array.isArray(county) ? county : [],
    country: Array.isArray(country) ? country : [],
  };
}

export function mapUserToClientProfileForm(user = {}) {
  const countryName = resolveProfileCountryName(user.country);
  return {
    first_name: user.first_name || user.firstname || "",
    last_name: user.last_name || user.lastname || "",
    address: user.address || "",
    country: countryName,
    postcode: user.postcode || "",
    ...mapPhoneFields(user, countryName),
  };
}

export function mapUserToDealerProfileForm(user = {}) {
  const rawType = user.type != null ? String(user.type) : "1";
  // Account role 6 should never appear on dealer form; default franchise.
  const businessType =
    rawType === String(USER_TYPE_CLIENT) || Number(rawType) > 5
      ? "1"
      : rawType;
  const countryName = resolveProfileCountryName(user.country);

  return {
    first_name: user.first_name || user.firstname || "",
    last_name: user.last_name || user.lastname || "",
    company_name: user.company_name || "",
    address: user.address || "",
    city: user.city || "",
    county: pickIdValue(user.county),
    country: countryName,
    postcode: user.postcode || "",
    garagecode: user.garagecode || "",
    business_type: businessType || "1",
    client_type:
      user.client_type != null && user.client_type !== ""
        ? String(user.client_type)
        : "2",
    ...mapPhoneFields(user, countryName),
  };
}

export function buildClientProfileFields(form) {
  return {
    first_name: form.first_name || "",
    last_name: form.last_name || "",
    company_name: "",
    type: USER_TYPE_CLIENT,
    address: form.address || "",
    country: countryToApiId(form.country),
    county: "",
    postcode: form.postcode || "",
    garagecode: "",
    mobile_number: joinPhone(form.phone_dial, form.phone_national),
    client_type: "",
  };
}

/**
 * Match legacy dealers profile PUT body, with client-style phone/country encoding.
 * `type` is the business category (1–5), same as the old dealers app.
 */
export function buildDealerProfileFields(form) {
  const businessType = String(form.business_type || "1");
  return {
    first_name: form.first_name || "",
    last_name: form.last_name || "",
    company_name: form.company_name || "",
    type: Number(businessType) || USER_TYPE_DEALER,
    address: form.address || "",
    city: form.city || "",
    country: countryToApiId(form.country),
    county: form.county === "" ? "" : Number(form.county) || form.county,
    postcode: form.postcode || "",
    garagecode: form.garagecode || "",
    mobile_number: joinPhone(form.phone_dial, form.phone_national),
    client_type: form.client_type || "",
  };
}

export function buildProfileBody(fields, imageValue) {
  if (imageValue instanceof File || imageValue instanceof Blob) {
    const body = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      body.append(key, value == null ? "" : String(value));
    });
    body.append("image", imageValue, imageValue.name || "profile.jpg");
    return body;
  }
  return fields;
}
